import t from "@ext/localization/locale/translate";
import type { OpenApiMessages } from "@gramax/openapi-viewer";

/**
 * Maps the openapi-viewer core's UI-chrome strings onto Gramax's own locale, so the renderer follows the app's current language.
 * The return type is the full `OpenApiMessages`, not a `Partial`: a key the core adds and Gramax forgets would otherwise fall back
 * to the core's English default inside a Russian article, so tsc is the gate that keeps the dictionary complete.
 */
const buildOpenApiMessages = (): OpenApiMessages => ({
	loading: t("openApi.loading"),
	noSourceHint: t("openApi.noSourceHint"),
	errorTitle: t("openApi.errorTitle"),
	errorInvalidFormat: t("openApi.errorInvalidFormat"),
	errorTechnicalDetails: t("openApi.errorTechnicalDetails"),
	authorize: t("openApi.authorize"),
	noResultsFound: t("openApi.noResultsFound"),
	operationsHeading: t("openApi.operationsHeading"),
	searchPlaceholder: t("openApi.searchPlaceholder"),
	searchResultsCount: t("openApi.searchResultsCount"),
	searchClearAriaLabel: t("openApi.searchClearAriaLabel"),
	serverVariableUnused: t("openApi.serverVariableUnused"),
	termsOfService: t("openApi.termsOfService"),
	contact: t("openApi.contact"),
	externalDocs: t("openApi.externalDocs"),
	docsLinkFallback: t("openApi.docsLinkFallback"),
	diagnosticsAriaLabel: t("openApi.diagnosticsAriaLabel"),
	diagnosticsErrors: t("openApi.diagnosticsErrors"),
	diagnosticsWarnings: t("openApi.diagnosticsWarnings"),
	diagnosticsInfo: t("openApi.diagnosticsInfo"),
	diagnosticPath: t("openApi.diagnosticPath"),
	diagnosticExpected: t("openApi.diagnosticExpected"),
	diagnosticReceived: t("openApi.diagnosticReceived"),
	diagnosticAction: t("openApi.diagnosticAction"),
	diagnosticVersionUnsupported: t("openApi.diagnosticVersionUnsupported"),
	diagnosticResponsesMissing: t("openApi.diagnosticResponsesMissing"),
	diagnosticResponsesNotObject: t("openApi.diagnosticResponsesNotObject"),
	diagnosticExternalRefUnresolved: t("openApi.diagnosticExternalRefUnresolved"),
	diagnosticInternalRefUnresolved: t("openApi.diagnosticInternalRefUnresolved"),
	diagnosticOperationNotObject: t("openApi.diagnosticOperationNotObject"),
	diagnosticPathItemNotObject: t("openApi.diagnosticPathItemNotObject"),
	diagnosticParametersNotArray: t("openApi.diagnosticParametersNotArray"),
	diagnosticParameterNotObject: t("openApi.diagnosticParameterNotObject"),
	diagnosticParameterNameNotString: t("openApi.diagnosticParameterNameNotString"),
	diagnosticParameterLocationNotString: t("openApi.diagnosticParameterLocationNotString"),
	diagnosticParameterSchemaNotObject: t("openApi.diagnosticParameterSchemaNotObject"),
	diagnosticContentNotObject: t("openApi.diagnosticContentNotObject"),
	diagnosticMediaTypeNotObject: t("openApi.diagnosticMediaTypeNotObject"),
	diagnosticMediaSchemaNotObject: t("openApi.diagnosticMediaSchemaNotObject"),
	diagnosticRequestBodyNotObject: t("openApi.diagnosticRequestBodyNotObject"),
	diagnosticResponseNotObject: t("openApi.diagnosticResponseNotObject"),
	diagnosticResponseHeadersNotObject: t("openApi.diagnosticResponseHeadersNotObject"),
	diagnosticResponseLinksNotObject: t("openApi.diagnosticResponseLinksNotObject"),
	diagnosticSchemasNotObject: t("openApi.diagnosticSchemasNotObject"),
	diagnosticSchemaNotObject: t("openApi.diagnosticSchemaNotObject"),
	diagnosticSecuritySchemesNotObject: t("openApi.diagnosticSecuritySchemesNotObject"),
	diagnosticSecuritySchemeNotObject: t("openApi.diagnosticSecuritySchemeNotObject"),
	diagnosticSecuritySchemeTypeInvalid: t("openApi.diagnosticSecuritySchemeTypeInvalid"),
	diagnosticSecuritySchemeFieldInvalid: t("openApi.diagnosticSecuritySchemeFieldInvalid"),
	diagnosticSecurityRequirementsNotArray: t("openApi.diagnosticSecurityRequirementsNotArray"),
	diagnosticSecurityRequirementNotObject: t("openApi.diagnosticSecurityRequirementNotObject"),
	diagnosticSecurityRequirementScopesNotArray: t("openApi.diagnosticSecurityRequirementScopesNotArray"),
	diagnosticSecurityRequirementUnknownScheme: t("openApi.diagnosticSecurityRequirementUnknownScheme"),
	diagnosticVersionMissing: t("openApi.diagnosticVersionMissing"),
	diagnosticInfoMissing: t("openApi.diagnosticInfoMissing"),
	permalink: t("openApi.permalink"),
	permalinkTo: t("openApi.permalinkTo"),
	deprecated: t("openApi.deprecated"),
	parametersTitle: t("openApi.parametersTitle"),
	requestTitle: t("openApi.requestTitle"),
	curlTitle: t("openApi.curlTitle"),
	modeDescribe: t("openApi.modeDescribe"),
	modeExecute: t("openApi.modeExecute"),
	requiredBadge: t("openApi.requiredBadge"),
	paramColumnName: t("openApi.paramColumnName"),
	paramColumnDescription: t("openApi.paramColumnDescription"),
	paramColumnValue: t("openApi.paramColumnValue"),
	paramDefaultLabel: t("openApi.paramDefaultLabel"),
	paramExampleLabel: t("openApi.paramExampleLabel"),
	sendEmptyValue: t("openApi.sendEmptyValue"),
	deepObjectHint: t("openApi.deepObjectHint"),
	fieldColumnName: t("openApi.fieldColumnName"),
	cookieParamsNotice: t("openApi.cookieParamsNotice"),
	cookieParamsNoticeDetail: t("openApi.cookieParamsNoticeDetail"),
	codeSamplesLabel: t("openApi.codeSamplesLabel"),
	sampleFallbackLabel: t("openApi.sampleFallbackLabel"),
	copyTitle: t("openApi.copyTitle"),
	contentTypeLabel: t("openApi.contentTypeLabel"),
	exampleTab: t("openApi.exampleTab"),
	schemaTab: t("openApi.schemaTab"),
	schemaAllowedValuesLabel: t("openApi.schemaAllowedValuesLabel"),
	schemaArrayItemsLabel: t("openApi.schemaArrayItemsLabel"),
	schemaAdditionalPropertiesLabel: t("openApi.schemaAdditionalPropertiesLabel"),
	schemaAdditionalPropertiesForbiddenLabel: t("openApi.schemaAdditionalPropertiesForbiddenLabel"),
	binaryTab: t("openApi.binaryTab"),
	requestBodyHeading: t("openApi.requestBodyHeading"),
	binaryBodyHint: t("openApi.binaryBodyHint"),
	plainBodyHint: t("openApi.plainBodyHint"),
	responsesHeading: t("openApi.responsesHeading"),
	responseColumnCode: t("openApi.responseColumnCode"),
	responseDefaultHint: t("openApi.responseDefaultHint"),
	noResponseBody: t("openApi.noResponseBody"),
	binaryResponseNotice: t("openApi.binaryResponseNotice"),
	formatLabel: t("openApi.formatLabel"),
	headersLabel: t("openApi.headersLabel"),
	headerColumnName: t("openApi.headerColumnName"),
	headerColumnType: t("openApi.headerColumnType"),
	linksLabel: t("openApi.linksLabel"),
	linkColumnName: t("openApi.linkColumnName"),
	operationColumnName: t("openApi.operationColumnName"),
	linkedOperationFallback: t("openApi.linkedOperationFallback"),
	parametersPrefix: t("openApi.parametersPrefix"),
	requestBodyPrefix: t("openApi.requestBodyPrefix"),
	authorizationTitle: t("openApi.authorizationTitle"),
	noAuthRequired: t("openApi.noAuthRequired"),
	noOauthFlowsDeclared: t("openApi.noOauthFlowsDeclared"),
	executeRequestButton: t("openApi.executeRequestButton"),
	executeSendingButton: t("openApi.executeSendingButton"),
	requiredFieldError: t("openApi.requiredFieldError"),
	requestFailedTitle: t("openApi.requestFailedTitle"),
	requestFailedHint: t("openApi.requestFailedHint"),
	emptyResponseLabel: t("openApi.emptyResponseLabel"),
	binaryResponseSummary: t("openApi.binaryResponseSummary"),
	secretShow: t("openApi.secretShow"),
	secretHide: t("openApi.secretHide"),
	requestDetailsLabel: t("openApi.requestDetailsLabel"),
	requestUrlLabel: t("openApi.requestUrlLabel"),
	resultHeading: t("openApi.resultHeading"),
	statusLabel: t("openApi.statusLabel"),
	clearButton: t("openApi.clearButton"),
	bodyLabel: t("openApi.bodyLabel"),
	schemasSectionName: t("openApi.schemasSectionName"),
	schemasSectionDesc: t("openApi.schemasSectionDesc"),
	modelLabel: t("openApi.modelLabel"),
	securitySchemesKicker: t("openApi.securitySchemesKicker"),
	closeLabel: t("openApi.closeLabel"),
	noAuthLabel: t("openApi.noAuthLabel"),
	oauthFooterNote: t("openApi.oauthFooterNote"),
	openIdConnectNote: t("openApi.openIdConnectNote"),
	mutualTlsLabel: t("openApi.mutualTlsLabel"),
	mutualTlsNote: t("openApi.mutualTlsNote"),
	httpAuthUnsupportedNote: t("openApi.httpAuthUnsupportedNote"),
	authVariantUnsupportedNote: t("openApi.authVariantUnsupportedNote"),
	authVariantConflictNote: t("openApi.authVariantConflictNote"),
	deprecatedAuthNote: t("openApi.deprecatedAuthNote"),
	requiredScopesLabel: t("openApi.requiredScopesLabel"),
	availableScopesLabel: t("openApi.availableScopesLabel"),
	bearerFormatLabel: t("openApi.bearerFormatLabel"),
	doneButton: t("openApi.doneButton"),
	bearerTokenLabel: t("openApi.bearerTokenLabel"),
	usernameLabel: t("openApi.usernameLabel"),
	passwordLabel: t("openApi.passwordLabel"),
	headerValueLabel: t("openApi.headerValueLabel"),
	queryValueLabel: t("openApi.queryValueLabel"),
	cookieValueLabel: t("openApi.cookieValueLabel"),
	apiKeyPlaceholder: t("openApi.apiKeyPlaceholder"),
	apiKeyNameLabel: t("openApi.apiKeyNameLabel"),
	apiKeyLocationLabel: t("openApi.apiKeyLocationLabel"),
	apiKeyHeaderHint: t("openApi.apiKeyHeaderHint"),
	apiKeyQueryHint: t("openApi.apiKeyQueryHint"),
	apiKeyCookieHint: t("openApi.apiKeyCookieHint"),
	apiKeyCookieRequestHint: t("openApi.apiKeyCookieRequestHint"),
	metadataOnlyNote: t("openApi.metadataOnlyNote"),
	oauthMetadataOnlyNote: t("openApi.oauthMetadataOnlyNote"),
	paramTipQuery: t("openApi.paramTipQuery"),
	paramTipPath: t("openApi.paramTipPath"),
	paramTipHeader: t("openApi.paramTipHeader"),
	paramTipCookie: t("openApi.paramTipCookie"),
	serverLabel: t("openApi.serverLabel"),
	baseUrlLabel: t("openApi.baseUrlLabel"),
	serverVariablesLabel: t("openApi.serverVariablesLabel"),
	operationServersLabel: t("openApi.operationServersLabel"),
	relativeUrlFallback: t("openApi.relativeUrlFallback"),
	serverSelectAriaLabel: t("openApi.serverSelectAriaLabel"),
	untaggedGroupLabel: t("openApi.untaggedGroupLabel"),
	specSwitcherAriaLabel: t("openApi.specSwitcherAriaLabel"),
	otherTagsLabel: t("openApi.otherTagsLabel"),
	swagger2HostLabel: t("openApi.swagger2HostLabel"),
});

export default buildOpenApiMessages;
